import { useCallback, useMemo, useState } from "react"
import { gql, useMutation } from "@apollo/client"
import dayjs from "dayjs"
import { DeliveryDestination, DeliveryDestinations } from "../../../../common/deliveryDestinations"
import { SupplierCard_SyncAliexpressSupplierProductMutation, SupplierCard_SyncAliexpressSupplierProductMutationVariables } from "../../../../codegen/graphql"
import { Modal, ModalAction } from "../../common/Modal"
import { useRequestManager } from "../../common/RequestManager"

const destinationCodes = Object.keys(DeliveryDestinations) as DeliveryDestination[]

export const SupplierCard_SupplierProduct = gql`
  fragment SupplierCard_SupplierProduct on SupplierProduct   {
    id
    supplierName
    name
    data
    externalProductId
    lastSyncAt
    note

    variations {
      sku
      supplierProductId
      data

      calculatedCost {
        destinationCountry
        totalCostIncludingVAT
        totalCostExcludingVAT
        totalCostVAT
        VATRate
        currency
      }
      availableStock {
        destinationCountry
        availableStock
      }
      quantityOrdered
      quantityRestocked

      typeValues {
        id
        type {
          id
          type
        }
        value
        extraData
      }

      adVariations {
        id
      }
    }
  }
`

const SupplierCard_SyncAliexpressSupplierProduct = gql`
  mutation SupplierCard_SyncAliexpressSupplierProduct(
    $productId: String!,
    $countries: [String!],
    $options: SyncAliexpressSupplierProductOptionsInput
  ) {
    syncAliexpressSupplierProduct(
      productId: $productId,
      countries: $countries,
      options: $options
    ) {
      ...SupplierCard_SupplierProduct
    }
  }
  ${SupplierCard_SupplierProduct}
`

export function SupplierCard_SyncAliexpressSupplier({
  productId,
  data,
  initialCountry,
  onClose,
}: {
  productId: string
  data: Record<string, { lastSyncAt?: string | null } | null | undefined>
  initialCountry: DeliveryDestination
  onClose: () => void
}) {
  const { trackRequest } = useRequestManager()
  const [selectedCountries, setSelectedCountries] = useState<DeliveryDestination[]>([initialCountry])

  const [syncAliexpressSupplierProduct] = useMutation<
    SupplierCard_SyncAliexpressSupplierProductMutation,
    SupplierCard_SyncAliexpressSupplierProductMutationVariables
  >(SupplierCard_SyncAliexpressSupplierProduct)

  const allSelected = selectedCountries.length === destinationCodes.length

  const toggleCountry = useCallback((code: DeliveryDestination) => {
    setSelectedCountries((prev) => (
      prev.includes(code) ? prev.filter((country) => country !== code) : [...prev, code]
    ))
  }, [])

  const toggleAll = useCallback(() => {
    setSelectedCountries(allSelected ? [] : destinationCodes)
  }, [allSelected])

  const handleResyncProduct = useCallback(() => {
    if (selectedCountries.length === 0) return

    trackRequest(
      syncAliexpressSupplierProduct({
        variables: {
          productId,
          countries: selectedCountries,
          options: {
            forceRefresh: true,
          },
        },
      }),
      {
        title: 'Synchronisation du fournisseur',
        isBlocking: false,
      }
    )
    onClose()
  }, [onClose, productId, selectedCountries, syncAliexpressSupplierProduct, trackRequest])

  const modalActions: ModalAction[] = useMemo(() => [
    {
      label: 'Annuler',
      onClick: onClose,
      preset: 'white',
    },
    {
      label: 'Synchroniser',
      onClick: handleResyncProduct,
      preset: 'primary',
      disabled: selectedCountries.length === 0,
    },
  ], [handleResyncProduct, onClose, selectedCountries.length])

  return (
    <Modal
      title="Synchroniser AliExpress"
      onClose={onClose}
      actions={modalActions}
      width="medium"
    >
      <div className="space-y-3">
        <p className="text-gray-600">
          Sélectionnez les pays à synchroniser.
        </p>
        <label className="flex items-center gap-3 pb-2 border-b cursor-pointer">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="font-medium">Tous les pays</span>
        </label>
        <div className="space-y-2">
          {destinationCodes.map((code) => {
            const destination = DeliveryDestinations[code]
            const countryData = data?.[code]
            const lastSyncLabel = countryData?.lastSyncAt
              ? dayjs(countryData.lastSyncAt).format('DD/MM/YYYY à HH:mm')
              : '(no data)'

            return (
              <label key={code} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCountries.includes(code)}
                  onChange={() => toggleCountry(code)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-lg">{destination.flag}</span>
                <span className="flex-1">{destination.name}</span>
                <span className="text-sm text-gray-500">{lastSyncLabel}</span>
              </label>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}
